import React, { useMemo } from 'react';
import MenuItem from '@components/catalog/catalog-hotfilters/catalog-category-hotfilter/menu-item/menu-item';

import { ElasticSearchFacet } from '@interfaces/api/responses/elasticSearch';
import useCategoryFilterFacets from '@hooks/filters/use-category-filter-facets';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import { CatalogCategoryHotfilterContext } from '@context/catalog-category-hotfilter.context';
import { facetToFilterRename } from '@maps/catalog/search-product-facets-filters-maps';
import { CatalogContext } from '@context/catalog.context';
import { ElasticSearchStringListFilterProperties } from '@interfaces/models/elasticSearch';

interface CategoryMenuItemProps {
  categoryFacet: ElasticSearchFacet;
  activeFilter: ElasticSearchStringListFilterProperties;
}

const CategoryMenuItem: React.FC<CategoryMenuItemProps> = ({ categoryFacet, activeFilter }) => {
  const menuState = useContextSelector(CatalogCategoryHotfilterContext, (context) => context.menuState);
  const setMenuState = useContextSelector(CatalogCategoryHotfilterContext, (context) => context.setMenuState);
  const childLevel = useContextSelector(CatalogCategoryHotfilterContext, (context) => context.childLevel);
  const removeCategoryHotfilter = useContextSelector(
    CatalogCategoryHotfilterContext,
    (context) => context.removeCategoryHotfilter,
  );
  const applyCategoryHotFilter = useContextSelector(
    CatalogCategoryHotfilterContext,
    (context) => context.applyCategoryHotFilter,
  );
  const filters = useContextSelector(CatalogContext, (context) => context.filters);
  const staticFilterGroups = useContextSelector(CatalogContext, (context) => context.staticFilterGroups);
  const filterGroup = menuState.activeLevel;
  const isSelected = filters[activeFilter]?.includes(categoryFacet.id);

  const childFacets = useCategoryFilterFacets({
    filterGroup: childLevel,
    parentId: categoryFacet.id,
    filterGroupSortBy: 'asc',
  });

  const hasChildren = childFacets.length > 0;
  const childFilterGroup = childLevel
    ? (facetToFilterRename(childLevel) as ElasticSearchStringListFilterProperties)
    : undefined;
  const isHighlighted = useMemo(
    () => childFacets.some((facet) => filters[childFilterGroup]?.includes(facet.id)),
    [childFacets, childFilterGroup, filters],
  );

  const handleClick = () => {
    if (!hasChildren) {
      if (isSelected) {
        removeCategoryHotfilter({
          id: categoryFacet.id,
          facet: filterGroup,
        });
        return;
      }
      applyCategoryHotFilter({
        id: categoryFacet.id,
        facet: filterGroup,
      });
      return;
    }
    setMenuState((prev) => ({
      ...prev,
      activeLevel: childLevel,
      [filterGroup]: {
        id: categoryFacet.id,
        name: categoryFacet.name,
      },
    }));
  };

  // if universe has no children, don't render it
  if (filterGroup === 'universe' && !hasChildren) {
    return null;
  }

  return (
    <MenuItem
      key={`category-menu-item-${categoryFacet.id}`}
      label={categoryFacet.name}
      handleClick={handleClick}
      hasChildren={hasChildren}
      isSelected={isSelected}
      isHighlighted={isHighlighted}
      isDisabled={!!staticFilterGroups[filterGroup]}
      dataCySelector={`${filterGroup}-${categoryFacet.id}-${hasChildren ? `arrow` : `filter`}`}
    />
  );
};

const CategoryMenuList: React.FC = () => {
  const menuState = useContextSelector(CatalogCategoryHotfilterContext, (context) => context.menuState);
  const parentLevel = useContextSelector(CatalogCategoryHotfilterContext, (context) => context.parentLevel);
  const applyCategoryHotFilter = useContextSelector(
    CatalogCategoryHotfilterContext,
    (context) => context.applyCategoryHotFilter,
  );
  const removeCategoryHotfilter = useContextSelector(
    CatalogCategoryHotfilterContext,
    (context) => context.removeCategoryHotfilter,
  );

  const filters = useContextSelector(CatalogContext, (context) => context.filters);
  const staticFilterGroups = useContextSelector(CatalogContext, (context) => context.staticFilterGroups);

  const filterGroup = menuState.activeLevel;
  const parentId = parentLevel ? menuState[parentLevel]?.id : undefined;
  const filterGroupSortBy = menuState.activeLevel === 'universe' ? 'sortById' : 'asc';
  const hasAllItems = ['categoryLvl0', 'categoryLvl1', 'categoryLvl2'].includes(menuState.activeLevel);
  const facets = useCategoryFilterFacets({ filterGroup, parentId, filterGroupSortBy });

  const { t } = useTranslation();

  const { parentFilter, activeFilter } = useMemo(() => {
    return {
      parentFilter: facetToFilterRename(parentLevel) as ElasticSearchStringListFilterProperties,
      activeFilter: facetToFilterRename(menuState.activeLevel) as ElasticSearchStringListFilterProperties,
    };
  }, [menuState, parentLevel]);

  const isAllItemsSelected = useMemo(() => {
    return filters[parentFilter]?.includes(menuState[parentLevel]?.id) && (filters[activeFilter] ?? []).length === 0;
  }, [activeFilter, filters, menuState, parentFilter, parentLevel]);

  const handleAllItemsClick = () => {
    if (isAllItemsSelected) {
      removeCategoryHotfilter({
        id: parentId,
        facet: parentLevel,
      });
      return;
    }
    applyCategoryHotFilter({
      id: parentId,
      facet: parentLevel,
    });
  };

  return (
    <div>
      {hasAllItems && (
        <MenuItem
          handleClick={handleAllItemsClick}
          hasChildren={false}
          isSelected={isAllItemsSelected}
          // TODO: update this key to a PLP one instead of reusing favourites one
          label={t('FAVORITE_PAGE.FILTER.SUB_CATEGORIES_ALL_ITEMS')}
          isDisabled={!!staticFilterGroups[parentLevel]}
          dataCySelector={`${parentLevel}-${parentId}-filter`}
        />
      )}
      {facets.map((facet) => (
        <CategoryMenuItem
          key={`${filterGroup}-menu-item-${facet.id}`}
          categoryFacet={facet}
          activeFilter={activeFilter}
        />
      ))}
    </div>
  );
};

export default CategoryMenuList;
